import "./cryptoPolyfill"; // must run before keycloak-js is evaluated
import Keycloak from "keycloak-js";
import { L } from "../engine/i18n";
import { icon } from "../kit/ui/icons";
import "./auth.css";

/**
 * Sign-in through the shared Keycloak identity provider (realm "smartlogistics", the same SSO as fleziLOG and the
 * Smart Logistics apps). Follows 01.fleziLOG/client/src/providers/KeycloakProvider.tsx: a silent check-sso in a hidden
 * iframe, PKCE S256, a watchdog so an unreachable provider never blocks the page, the mixed-content guard, and a token
 * refresh every 30 s. This app has no backend, so the signed-in user is read from the ID token instead of a session.
 *
 * VITE_AUTH_REQUIRED=false turns the gate off (tests, offline demos); everything else comes from .env (see .env.example).
 */
export const AUTH_REQUIRED = import.meta.env.VITE_AUTH_REQUIRED !== "false";

const CONFIGURED_SSO_URL: string =
  import.meta.env.VITE_KEYCLOAK_URL ||
  // Same development provider as fleziLOG. Plain HTTP: an https:// deployment needs VITE_KEYCLOAK_URL set to an https
  // address (or a same-origin /realms/ proxy, as fleziLOG's nginx does).
  // eslint-disable-next-line sonarjs/no-clear-text-protocols
  "http://ec2-54-254-75-238.ap-southeast-1.compute.amazonaws.com:3035";

// Served over https (https://manu.flezilog.com) the plain-http provider is mixed content: the login redirect still
// navigates, but the token exchange is an XHR the browser drops silently, so the user signs in and lands on a dead
// page. fleziLOG's nginx proxies /realms/ and /resources/ on every app subdomain, so use this origin instead. Keyed
// off the page protocol, not a build flag: the same bundle keeps working on plain http://…:1013.
export const SSO_URL: string =
  typeof window !== "undefined" && window.location.protocol === "https:" && CONFIGURED_SSO_URL.startsWith("http://")
    ? window.location.origin
    : CONFIGURED_SSO_URL;
const REALM = import.meta.env.VITE_KEYCLOAK_REALM || "smartlogistics";
const CLIENT = import.meta.env.VITE_KEYCLOAK_CLIENT || "smart-manufacturing";

const REFRESH_INTERVAL = 30_000; // check every 30 s…
const MIN_VALIDITY = 60; // …and refresh when the token expires within 60 s
const INIT_TIMEOUT = 10_000; // give up on the silent SSO check after 10 s
const RETURN_KEY = "sf-auth-return"; // the page to come back to after the Keycloak round trip

export interface User { name: string; email: string; username: string; roles: string[] }
export interface AuthState {
  /** SSO init reached the provider (false when it is unreachable, blocked or timed out). */
  available: boolean;
  authenticated: boolean;
  user: User | null;
}

const keycloak = new Keycloak({ url: SSO_URL, realm: REALM, clientId: CLIENT });
let state: AuthState = { available: false, authenticated: false, user: null };

/** True when this page's scheme makes the identity provider unreachable (https page, http provider). */
export const ssoBlockedByMixedContent = (): boolean => location.protocol === "https:" && SSO_URL.startsWith("http://");

function readUser(): User | null {
  const t = keycloak.tokenParsed as Record<string, unknown> | undefined;
  if (!t) return null;
  const s = (k: string) => (typeof t[k] === "string" ? (t[k] as string) : "");
  const access = (t.resource_access as Record<string, { roles?: string[] }> | undefined)?.[CLIENT]?.roles ?? [];
  const realm = (t.realm_access as { roles?: string[] } | undefined)?.roles ?? [];
  const name = s("name") || [s("given_name"), s("family_name")].filter(Boolean).join(" ") || s("preferred_username") || s("email");
  return { name, email: s("email"), username: s("preferred_username"), roles: [...access, ...realm] };
}

/** Starts the silent SSO check; resolves within INIT_TIMEOUT whatever the provider does. */
export async function startAuth(): Promise<AuthState> {
  // gate off: stay anonymous and never touch the provider, unless the visitor asks for #/login or is coming back from it
  const returning = /[?&]state=/.test(location.search) && /[?&](code|error)=/.test(location.search);
  if (!AUTH_REQUIRED && location.hash !== "#/login" && !returning) return state;
  if (ssoBlockedByMixedContent()) {
    console.warn("[SSO] identity provider is http:// but this page is https:// — skipping SSO init (mixed content)");
    return state;
  }
  const init = keycloak
    .init({
      onLoad: "check-sso",
      // check inside a hidden iframe, never by navigating the whole page (see public/silent-check-sso.html)
      silentCheckSsoRedirectUri: `${location.origin}${location.pathname.replace(/[^/]*$/, "")}silent-check-sso.html`,
      checkLoginIframe: false,
      // when the browser blocks third-party cookies keycloak-js would retry the check as a full-page redirect — on an
      // origin the client does not list that strands the visitor on a Keycloak error page; show our sign-in page instead
      silentCheckSsoFallback: false,
      pkceMethod: "S256",
      // the app routes with the URL hash, so Keycloak must answer in the query string, not the fragment
      responseMode: "query",
    })
    .then(authenticated => {
      state = { available: true, authenticated, user: authenticated ? readUser() : null };
      if (authenticated) {
        setInterval(() => { keycloak.updateToken(MIN_VALIDITY).then(r => { if (r) state.user = readUser(); }).catch(() => console.warn("[SSO] token refresh failed, session expired")); }, REFRESH_INTERVAL);
        restoreReturn();
      }
      return state;
    })
    .catch(err => { console.error("[SSO] Keycloak init failed:", err); return state; });
  const watchdog = new Promise<AuthState>(res => setTimeout(() => { console.warn(`[SSO] Keycloak init did not resolve within ${INIT_TIMEOUT} ms`); res(state); }, INIT_TIMEOUT));
  return Promise.race([init, watchdog]);
}

export const authState = (): AuthState => state;

/** Sends the browser to the Keycloak sign-in page and brings it back to `returnTo` (the current page by default). */
export function login(returnTo = location.hash): void {
  if (!state.available) return;
  try { sessionStorage.setItem(RETURN_KEY, returnTo && returnTo !== "#/login" ? returnTo : "#/"); } catch { /* private mode */ }
  void keycloak.login({ redirectUri: location.origin + location.pathname + location.search.replace(/[?&]loggedout=1/, "") });
}

/** Signs out of Keycloak (every app on the SSO) and comes back to the sign-in page. */
export function logout(): void {
  state = { ...state, authenticated: false, user: null };
  if (keycloak.authenticated) void keycloak.logout({ redirectUri: `${location.origin}${location.pathname}?loggedout=1#/login` });
  else location.hash = "#/login";
}

function restoreReturn(): void {
  let to: string | null = null;
  try { to = sessionStorage.getItem(RETURN_KEY); sessionStorage.removeItem(RETURN_KEY); } catch { /* ignore */ }
  if (to && to !== location.hash) history.replaceState(null, "", to);
  else if (location.hash === "#/login") history.replaceState(null, "", "#/");
}

const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join("") || "?";
const escape = (s: string) => s.replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

/** Header chip of the signed-in user (name + sign-out); a sign-in button when SSO is optional and nobody is signed in. */
export function userChip(): string {
  const u = state.user;
  if (!u) return !AUTH_REQUIRED ? `<a class="btn user-in" href="#/login">${icon("key", 15)}<span>${L("Đăng nhập", "Sign in", "로그인")}</span></a>` : "";
  const out = L("Đăng xuất", "Sign out", "로그아웃");
  return `<div class="user-chip" title="${escape(u.email || u.username)}"><span class="av">${escape(initials(u.name))}</span><span class="nm">${escape(u.name)}</span>` +
    `<button type="button" class="out" data-logout title="${out}" aria-label="${out}">${icon("back", 15)}</button></div>`;
}

// one listener for every page: the chip is re-rendered with the headers
document.addEventListener("click", e => { if ((e.target as Element).closest?.("[data-logout]")) logout(); });
